/**
 * Unified brand-colored loading spinner used across the entire system.
 *
 * Visual contract (single source of truth):
 *   muted track  +  brand-colored arc  +  rotation
 *
 * Color follows the active tenant via the `--brand` CSS variable which
 * `buildTenantStyle()` injects on academy mount. In Admin (no tenant
 * override) it falls back to the platform brand.
 *
 * Sizes:
 *   sm  → 24px (border-2)   inline / small cards
 *   md  → 40px (border-[3px]) sections / dialogs
 *   lg  → 64px (border-4)   full content area
 */
const SIZE_MAP = {
  sm: { box: "w-6 h-6", border: "border-2" },
  md: { box: "w-10 h-10", border: "border-[3px]" },
  lg: { box: "w-16 h-16", border: "border-4" },
};

export function BrandSpinner({ size = "md", className = "" }) {
  const { box, border } = SIZE_MAP[size] || SIZE_MAP.md;
  return (
    <div
      className={`relative ${box} ${className}`}
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <span className="sr-only">Loading…</span>
      <div className={`absolute inset-0 rounded-full ${border} border-muted/40`} />
      <div
        className={`absolute inset-0 rounded-full ${border} border-transparent border-t-brand border-r-brand animate-spin`}
      />
    </div>
  );
}

/**
 * Full content-area centered spinner used as Suspense fallback inside
 * authenticated layouts. Sits to the side of the sidebar/topbar.
 */
export default function ContentSpinner({ className = "" }) {
  return (
    <div
      className={`flex items-center justify-center w-full min-h-[calc(100vh-3.5rem-4rem)] ${className}`}
    >
      <BrandSpinner size="lg" />
    </div>
  );
}
